import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'
import { useUserOrgs } from './useUserOrgs'

export type LicenseEntitlement = {
  license_id: string
  scope: 'personal' | 'organization'
  org_id: string | null
  org_name: string | null
  license_tier: string
  expires_at: string
  total_devices: number
  active_devices: number
  annual_quota_km2: number
  used_km2: number
  remaining_km2: number
  maps_generated_this_year: number
}

async function fetchEntitlements(
  orgIds: string[]
): Promise<LicenseEntitlement[]> {
  const contexts: Array<string | null> = [null, ...orgIds]
  const results = await Promise.all(
    contexts.map(async (orgId) => {
      const { data, error } = await supabase.rpc('get_license_entitlements', {
        p_org_id: orgId,
      })
      if (error) throw error
      return (data ?? []) as LicenseEntitlement[]
    })
  )

  const [personalEntitlements = [], ...organizationResults] = results
  const unique = new Map<string, LicenseEntitlement>(
    personalEntitlements.map((entitlement) => [entitlement.license_id, entitlement])
  )

  for (const entitlement of organizationResults.flat()) {
    // Organization contexts without an organization license return the
    // personal entitlement as a fallback with context-specific legacy usage.
    // Keep the canonical personal-context row instead of overwriting it.
    if (entitlement.scope === 'organization') {
      unique.set(entitlement.license_id, entitlement)
    }
  }

  return [...unique.values()].sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === 'organization' ? -1 : 1
    return a.license_id.localeCompare(b.license_id)
  })
}

export function useLicenseEntitlements() {
  const { user } = useAuth()
  const { orgs, loading: orgsLoading } = useUserOrgs()
  const orgIds = orgs.map((org) => org.id).sort()

  const query = useQuery({
    queryKey: ['license-entitlements', user?.id, orgIds],
    queryFn: () => fetchEntitlements(orgIds),
    enabled: !!user && !orgsLoading,
  })

  return {
    entitlements: query.data ?? [],
    loading: orgsLoading || query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
